"use client";

import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useAnalytics, getPlaySource } from '@/hooks/useAnalytics';

// Tipos para o contexto de áudio
export interface AudioState {
  isPlaying: boolean;
  currentPodcastId: number | null;
  currentTime: number;
  duration: number;
  volume: number;
  loading: boolean;
  error: string | null;
}

export interface AudioControls {
  play: (podcastId: number, audioUrl: string) => void;
  pause: () => void;
  toggle: (podcastId: number, audioUrl: string) => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  stop: () => void;
}

interface AudioContextType {
  state: AudioState;
  controls: AudioControls;
  audioRef: React.RefObject<HTMLAudioElement | null>;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

// Hook para usar o contexto de áudio
export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider');
  }
  return context;
};

// Provider do contexto de áudio
export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const analytics = useAnalytics();
  
  const [state, setState] = useState<AudioState>({
    isPlaying: false,
    currentPodcastId: null,
    currentTime: 0,
    duration: 0,
    volume: 0.8,
    loading: false,
    error: null,
  });

  // Atualizar tempo atual
  const updateTime = useCallback(() => {
    if (audioRef.current) {
      const currentTime = audioRef.current.currentTime;
      const duration = audioRef.current.duration || 0;
      
      setState(prev => ({
        ...prev,
        currentTime,
        duration,
      }));

      // Track progress for analytics
      if (state.currentPodcastId && duration > 0) {
        analytics.trackPlayProgress(currentTime, duration);
      }
    }
  }, [state.currentPodcastId, analytics]);

  // Função para atualizar duração no banco de dados
  const updateDurationInDatabase = useCallback(async (podcastId: number, duration: number) => {
    try {
      const response = await fetch(`/api/podcast/${podcastId}/update-duration`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ duration }),
      });

      if (!response.ok) {
        console.warn('⚠️ Falha ao atualizar duração no banco:', response.statusText);
      } else {
        console.log(`✅ Duração atualizada no banco para podcast ${podcastId}: ${Math.round(duration)}s`);
      }
    } catch (error) {
      console.warn('⚠️ Erro ao atualizar duração no banco:', error);
    }
  }, []);

  // Event listeners para o elemento de áudio
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadStart = () => {
      setState(prev => ({ ...prev, loading: true, error: null }));
    };

    const handleCanPlay = () => {
      setState(prev => ({ ...prev, loading: false }));
    };

    const handlePlay = () => {
      setState(prev => ({ ...prev, isPlaying: true }));
      
      // Track play start when audio actually starts playing
      if (state.currentPodcastId && audio.duration) {
        const playSource = getPlaySource();
        analytics.trackPlayStart(state.currentPodcastId, audio.duration, playSource);
      }
    };

    const handlePause = () => {
      setState(prev => ({ ...prev, isPlaying: false }));
      
      // Track pause event
      if (state.currentPodcastId && audio.duration) {
        analytics.trackPlayPause(audio.currentTime, audio.duration);
      }
    };

    const handleEnded = () => {
      setState(prev => ({ 
        ...prev, 
        isPlaying: false, 
        currentTime: 0, 
        currentPodcastId: null 
      }));
      
      // Track play end when audio finishes
      if (state.currentPodcastId && audio.duration) {
        analytics.trackPlayEnd(audio.currentTime, audio.duration);
      }
    };

    const handleError = () => {
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: 'Erro ao carregar o áudio',
        isPlaying: false 
      }));
    };

    const handleTimeUpdate = updateTime;

    // Adicionar event listeners
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      // Remover event listeners
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [updateTime, state.currentPodcastId, analytics]);

  // Controles de áudio
  const controls: AudioControls = {
    play: useCallback((podcastId: number, audioUrl: string) => {
      const audio = audioRef.current;
      if (!audio) return;

      try {
        // Se é um podcast diferente, trocar a fonte
        if (state.currentPodcastId !== podcastId) {
          audio.src = audioUrl;
          setState(prev => ({ 
            ...prev, 
            currentPodcastId: podcastId,
            currentTime: 0,
            error: null 
          }));
        }

        audio.play();
      } catch (error) {
        setState(prev => ({ 
          ...prev, 
          error: 'Erro ao reproduzir o áudio' 
        }));
      }
    }, [state.currentPodcastId]),

    pause: useCallback(() => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
      }
    }, []),

    toggle: useCallback((podcastId: number, audioUrl: string) => {
      if (state.isPlaying && state.currentPodcastId === podcastId) {
        controls.pause();
      } else {
        controls.play(podcastId, audioUrl);
      }
    }, [state.isPlaying, state.currentPodcastId]),

    seek: useCallback((time: number) => {
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = time;
        setState(prev => ({ ...prev, currentTime: time }));
      }
    }, []),

    setVolume: useCallback((volume: number) => {
      const audio = audioRef.current;
      if (audio) {
        audio.volume = volume;
        setState(prev => ({ ...prev, volume }));
      }
    }, []),

    stop: useCallback(() => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
        setState(prev => ({ 
          ...prev, 
          isPlaying: false, 
          currentTime: 0, 
          currentPodcastId: null 
        }));
      }
    }, []),
  };

  return (
    <AudioContext.Provider value={{ state, controls, audioRef }}>
      {children}
      {/* Elemento de áudio oculto */}
      <audio
        ref={audioRef}
        preload="metadata"
        style={{ display: 'none' }}
      />
    </AudioContext.Provider>
  );
};
